import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';

import ExpandingCard from '../ExpandingCard/ExpandingCard';
import { inspectionDb, clearLog, fetchJson } from '../Helpers/Helpers';
import config from '../../config';

import './Log.css';

import { Button } from '@mui/material';

/**
 * View component that lists inspections stored in the IndexedDB outbox table
 * with the option to show their entire contents, and delete them from the
 * outbox.
 */
function Log() {
	const { t } = useSelector(redux => redux.store)
	const [inspections, setInspections] = useState([]);
	const [log, setLog] = useState([]);
	const [serverlog, setServerlog] = useState([]);
	const [emptyOutbox, setEmptyOutbox] = useState(false);
	const [emptyLog, setEmptyLog] = useState(false);
	const [emptyServerlog, setEmptyServerlog] = useState(false);

	useEffect(() => {
		getInspectionsFromOutbox();
		getClientLog();
		getServerLog();
	}, []);

	/**
	 * Retrieves any remaining inspections from the outbox (indexedDb) and
	 * stores it in the component state
	 */
	async function getInspectionsFromOutbox() {
		const inspections = await inspectionDb
			.then(db => {
				return db.transaction('outbox', 'readwrite')
					.objectStore('outbox')
					.getAll();
			})
		setInspections(inspections);
		setEmptyOutbox(!inspections.length);
	}

	/**
	 * Retrieves the client log from indexedDb and stores it in the component
	 * state
	 */
	async function getClientLog() {
		const log = await inspectionDb
			.then(db => {
				return db.transaction('log', 'readwrite')
					.objectStore('log')
					.getAll();
			})
		setLog(log);
		setEmptyLog(!log.length);
	}

	/**
	 * Retrieves the server log from the BFF, maps it to a structure for easy
	 * rendering and stores it in the component state
	 */
	async function getServerLog() {
		fetchJson(`serverlog`)
			.then(_response => { // prefixed with underscore to prevent conflict with response property
				// map response to have a specific details property
				const _mappedResponse = _response.map(entry => {
					return {
						_id: entry._id,
						type: entry.type,
						timestamp: entry.timestamp,
						message: entry.message,
						details: { ...entry }
					}
				})
				setServerlog(_mappedResponse)
				setEmptyServerlog(!_response.length)
			}).catch(err => {
				console.error(err)
			});
	}

	/**
	 * Deletes an inspection from indexedDb. Renders a warning first, if
	 * confirmed, also rerenders the outbox log
	 *
	 * @param  {String} key
	 */
	async function deleteInspectionFromOutbox(key) {
		if (window.confirm(t.log_delete_outbox_confirm)) {
			await inspectionDb.then(db => {
				return db.transaction('outbox', 'readwrite')
					.objectStore('outbox')
					.delete(key);
			})
			await getInspectionsFromOutbox();
		}
	}

	/**
	 * Clears the client log from indexedDb. Renders a warning first, if
	 * confirmed, also rerenders the client log
	 */
	async function clearClientLog() {
		if (window.confirm(t.log_clear_log_confirm)) {
			await clearLog();
			await getClientLog();
		}
	}

	return (
		<div className="log__wrapper">
			<h2>{ t.log_name_outbox }</h2>
			{ inspections.map(inspection => (
				<ExpandingCard
					key={ inspection.key }
					title={ inspection.data.installation.fields.building_name.text }
					subtitle={ `${ t.inspection } ID: ${inspection.data.inspection_id}` }>
					<textarea
						readOnly
						className="log__textarea"
						value={ JSON.stringify(inspection.data, null, 4) } />
					<Button
						variant="contained"
						color="primary"
						onClick={ () => { deleteInspectionFromOutbox(inspection.key) } }>
						{ t.log_delete_from_outbox_warning }: { inspection.data.inspection_id }
					</Button>
				</ExpandingCard>
			))}
			{ emptyOutbox &&
				<p>{ t.log_name_outbox } { t.log_empty }</p>
			}
			<h2>{ t.log_name_client_log }</h2>
			{ !emptyLog &&
				<LogOutput
					log={ log }
					dateField="key"
					clearCallback={ () => { clearClientLog() } } />
			}
			{ emptyLog &&
				<p>{ t.log_name_client_log } { t.log_empty }</p>
			}
			<h2>{ t.log_name_server_log }</h2>
			{ !emptyServerlog &&
				<LogOutput
					log={ serverlog }
					keyField="_id" />
			}
			{ emptyServerlog &&
				<p>{ t.log_name_server_log } { t.log_empty }</p>
			}
		</div>
	);
}

/**
 * Render a log entry. Same structure for client and server logs.
 *
 * Props:
 *  - log           => the full log object
 *  - keyField      => the keyfield for the iterator ('key' by default)
 *  - dateField     => the date field ('timestamp' by default)
 *  - clearCallback => a callback function, if provided, renders a button that calls the callback
 *
 * @param {Object} props
 */
function LogOutput(props) {
	const { t } = useSelector(redux => redux.store)

	/**
	 * Renders a link directly to the API endpoint that shows a specific
	 * property on a queried server log entry
	 *
	 * @param {Object} props
	 */
	function LogObjectLink(props) {
		return (
			<span>
				<a
					href={ `${config.api.url}/serverlog/${props.logEntry._id}?property=${props.object}` }
					rel="noopener noreferrer"
					target="_blank"
				>
					{ t.log_open } { props.object } { t.log_in_new_tab }
				</a>
				<br/>
			</span>
		)
	}

	return (
		<div>
			<pre
				className="log__logentries"
			>
				{ props.log.map(logEntry => (
					<details
						className={ `log__logentries-line log__logentries-line--${logEntry.type}` }
						key={ logEntry[props.keyField || 'key'] }
					>
						<summary>
							<span
								className="log__logentries-date"
							>
								{ new Date(logEntry[props.dateField || 'timestamp']).toUTCString() }
							</span>
							{ logEntry.message }
						</summary>
						{ logEntry.details &&
							<div
								className="log__logentries-details"
							>
								url      { logEntry.details.url }
								<br/>
								method   { logEntry.details.method }
								<br/>
								status   { logEntry.details.status }
								<br/>
								objectid { logEntry.details.objectId }
								<br/>
								userid   { logEntry.details.userId }
								<br/>
								<LogObjectLink
									logEntry={ logEntry }
									object="payload"
								/>
								<LogObjectLink
									logEntry={ logEntry }
									object="response"
								/>
							</div>
						}
						{! logEntry.details &&
							<div
								className="log__logentries-details"
							>
								{ t.iapp_log_missing_details }
							</div>
						}
					</details>
				))}
			</pre>
			{ props.clearCallback &&
			<Button
				variant="contained"
				color="primary"
				onClick={ props.clearCallback }>
				{ t.log_clear_log_action }
			</Button>
			}
		</div>
	);
}

export default Log;
